import { useQuery } from '@tanstack/vue-query';
import { useUserStore } from '@store/user';
import { useTimesheetStore } from '@store/timesheet';
import TimesheetAPI from '@api/timesheet';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

// Exported so a clock-out that bypasses the timesheet store can refresh the pill
export const ON_SHIFT_QUERY_KEY = 'timesheets/current-timesheet-entry/mine';

/**
 * Whether the signed-in user has an open clock-in today, read from their own entry. The shared
 * timesheet store can hold another staff member's entry (`/staff/timesheets/:id`), so it only
 * triggers a refetch here.
 */
export function useOnShift() {
    const userStore = useUserStore();
    const timesheetStore = useTimesheetStore();

    const { data: entry, refetch } = useQuery({
        queryKey: computed(() => [ON_SHIFT_QUERY_KEY, userStore.tenantId, userStore.getUserId]),
        queryFn: async () => {
            const response = await TimesheetAPI.getCurrentTimesheetEntry({
                user_id: userStore.getUserId,
                date: dayjs()
                    .tz(userStore.getTimezone || dayjs.tz.guess())
                    .format('YYYY-MM-DD'),
            });

            return response.data ?? null;
        },
        retry: false,
        // A clock-in from the crew app shows once the user is back on this tab
        refetchOnWindowFocus: true,
    });

    watch(
        () => timesheetStore.getCurrentTimesheetEntry,
        () => refetch(),
    );

    return computed(() => !!(entry.value?.time_in && !entry.value.time_out));
}
